import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Table, Badge, Button, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaUsers, FaDollarSign, FaChartLine, FaExclamationTriangle, FaBan, FaPaperPlane, FaPlus } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useTheme } from '../../../context/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  adminFetchStats,
  adminFetchSubscriptions,
  adminCancelSubscription,
  adminFetchPlans,
  adminCreateInvitation,
} from '../../../features/subscriptions/subscriptionsSlice';
import usersService from '../../../features/users/usersService';
import type { User } from '../../../features/users/usersSlice';

const STATUS_VARIANTS: Record<string, string> = {
  active: 'success',
  trialing: 'info',
  past_due: 'warning',
  canceled: 'danger',
  incomplete: 'secondary',
  unpaid: 'danger',
  pending_payment: 'warning',
};

const SubscriptionsDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const dispatch = useAppDispatch();
  const { adminStats, adminSubscriptions, adminSubscriptionsPagination, adminPlans, isLoading, error } = useAppSelector(
    (state) => state.subscriptions,
  );

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteUserId, setInviteUserId] = useState<number | ''>('');
  const [invitePlanId, setInvitePlanId] = useState<number | ''>('');
  const [inviting, setInviting] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    dispatch(adminFetchStats());
    dispatch(adminFetchPlans());
  }, [dispatch]);

  useEffect(() => {
    dispatch(adminFetchSubscriptions({ page, page_size: 20, status: statusFilter || undefined }));
  }, [dispatch, page, statusFilter]);

  const openInviteModal = async () => {
    setShowInviteModal(true);
    setInviteUserId('');
    setInvitePlanId('');
    setLoadingUsers(true);
    try {
      const resp = await usersService.getUsers({ page: 1, limit: 500 });
      const filtered = resp.users.filter((u: User) =>
        u.roles.some((r: string) => r === 'company_owner' || r === 'agency_owner'),
      );
      setEligibleUsers(filtered);
    } catch { /* ignore */ } finally {
      setLoadingUsers(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteUserId || !invitePlanId) return;
    setInviting(true);
    try {
      await dispatch(adminCreateInvitation({
        user_id: inviteUserId as number,
        plan_id: invitePlanId as number,
      })).unwrap();
      setShowInviteModal(false);
      setInviteUserId('');
      setInvitePlanId('');
      dispatch(adminFetchSubscriptions({ page, page_size: 20, status: statusFilter || undefined }));
      dispatch(adminFetchStats());
      Swal.fire({
        title: t('success', 'Success'),
        text: t('invitation_sent_success', 'Invitation sent successfully!'),
        icon: 'success',
        timer: 2500,
        showConfirmButton: false,
        allowOutsideClick: true,
      });
    } catch {
      // error displayed via redux state
    } finally {
      setInviting(false);
    }
  };

  const handleCancel = async (subId: number) => {
    if (window.confirm(t('confirm_cancel_subscription', 'Are you sure you want to cancel this subscription?'))) {
      await dispatch(adminCancelSubscription({ id: subId, atPeriodEnd: true }));
      dispatch(adminFetchSubscriptions({ page, page_size: 20, status: statusFilter || undefined }));
      dispatch(adminFetchStats());
    }
  };

  const selectedUser = useMemo(() => {
    if (!inviteUserId) return null;
    return eligibleUsers.find((u) => u.id === inviteUserId) || null;
  }, [inviteUserId, eligibleUsers]);

  const statCurrency = adminStats?.currency || 'usd';

  const formatPrice = (amount: number, currency?: string) => {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || statCurrency).toUpperCase() }).format(amount);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: 20,
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden',
  };

  const statCardStyle: React.CSSProperties = {
    background: 'var(--bg-primary)',
    borderRadius: 20,
    padding: '1.25rem 1.5rem',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow-sm)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const statIconStyle = (color: string): React.CSSProperties => ({
    width: 44, height: 44, borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: isDark ? `${color}22` : `${color}15`,
    color: color,
    fontSize: '1.2rem',
    transition: 'all 0.3s ease',
    flexShrink: 0,
  });

  const statItems = [
    { label: t('active_subscriptions', 'Active'), value: adminStats?.active_subscriptions ?? '—', icon: FaUsers, color: '#22c55e' },
    { label: t('mrr', 'MRR'), value: adminStats ? formatPrice(adminStats.mrr) : '—', icon: FaChartLine, color: '#8b5cf6' },
    { label: t('total_revenue', 'Total Revenue'), value: adminStats ? formatPrice(adminStats.total_revenue) : '—', icon: FaDollarSign, color: '#3b82f6' },
    { label: t('past_due', 'Past Due'), value: adminStats?.past_due_subscriptions ?? '—', icon: FaExclamationTriangle, color: '#f59e0b' },
  ];

  return (
    <div className="animate-fade-in" style={{ padding: '1.5rem' }}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h3 className="fw-bold mb-0" style={{ color: 'var(--text-primary)' }}>
          {t('subscriptions_dashboard', 'Subscriptions Dashboard')}
        </h3>
        <Button variant="primary" size="sm" className="btn-modern btn-primary" onClick={openInviteModal} style={{ width: '250px', borderRadius: 10, padding: '0.4rem 1rem', fontSize: '0.82rem' }}>
          <FaPlus className="me-1" /> {t('assign_subscription', 'Assign Subscription')}
        </Button>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <Row className="g-3 mb-4">
        {statItems.map((item, idx) => (
          <Col sm={6} lg={3} key={idx}>
            <div style={statCardStyle} className="d-flex align-items-center gap-3">
              <div style={statIconStyle(item.color)}>
                <item.icon />
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {item.label}
                </div>
                <div style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.2 }}>
                  {item.value}
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <div style={cardStyle}>
        <div
          className="d-flex align-items-center justify-content-between"
          style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid var(--border-color)',
            background: 'var(--bg-secondary)',
          }}
        >
          <h6 className="mb-0 fw-bold" style={{ color: 'var(--text-primary)' }}>
            {t('all_subscriptions', 'All Subscriptions')}
          </h6>
          <Form.Select
            size="sm"
            style={{
              width: 160,
              background: 'var(--bg-elevated)',
              color: 'var(--text-primary)',
              borderColor: 'var(--border-color)',
              borderRadius: 10,
            }}
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">{t('all_statuses', 'All Statuses')}</option>
            <option value="active">{t('active', 'Active')}</option>
            <option value="trialing">{t('trialing', 'Trialing')}</option>
            <option value="pending_payment">{t('status_pending_payment', 'Pending Payment')}</option>
            <option value="past_due">{t('past_due', 'Past Due')}</option>
            <option value="canceled">{t('canceled', 'Canceled')}</option>
            <option value="unpaid">{t('unpaid', 'Unpaid')}</option>
          </Form.Select>
        </div>

        {isLoading && adminSubscriptions.length === 0 ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: 'var(--brand-primary)' }} />
          </div>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                {[t('user', 'User'), t('company', 'Company'), t('plan', 'Plan'), t('status', 'Status'), t('period', 'Period'), t('created', 'Created'), t('actions', 'Actions')].map((label) => (
                  <th key={label} style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.02em' }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adminSubscriptions.map((sub) => (
                <tr key={sub.id}>
                  <td style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                    {sub.user_name || '—'}
                  </td>
                  <td style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                    {sub.company_name || `Company #${sub.company_id}`}
                  </td>
                  <td style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>{sub.plan?.name || '—'}</td>
                  <td style={{ borderColor: 'var(--border-color)' }}>
                    <Badge bg={STATUS_VARIANTS[sub.status] || 'secondary'}>
                      {t(`status_${sub.status}`, sub.status)}
                    </Badge>
                    {sub.cancel_at_period_end && (
                      <Badge bg="warning" text="dark" className="ms-1">{t('canceling', 'Canceling')}</Badge>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)', fontSize: '0.85rem' }}>
                    {sub.status === 'pending_payment'
                      ? '—'
                      : `${formatDate(sub.current_period_start)} – ${formatDate(sub.current_period_end)}`}
                  </td>
                  <td style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)', fontSize: '0.85rem' }}>
                    {formatDate(sub.created_at)}
                  </td>
                  <td style={{ borderColor: 'var(--border-color)' }}>
                    {['active', 'trialing', 'past_due'].includes(sub.status) && !sub.cancel_at_period_end && (
                      <Button variant="outline-danger" size="sm" className="btn-modern" onClick={() => handleCancel(sub.id)} style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem', borderRadius: 8 }}>
                        <FaBan className="me-1" /> {t('cancel', 'Cancel')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {adminSubscriptions.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-4" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}>
                    {t('no_subscriptions', 'No subscriptions found')}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}

        {adminSubscriptionsPagination && adminSubscriptionsPagination.total_pages > 1 && (
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border-color)',
              background: 'var(--bg-tertiary)',
            }}
          >
            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              {t('showing_page', 'Page {{page}} of {{total}}', {
                page: adminSubscriptionsPagination.current_page,
                total: adminSubscriptionsPagination.total_pages,
              })}
            </span>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                className="btn-modern btn-secondary"
                disabled={!adminSubscriptionsPagination.has_previous}
                onClick={() => setPage(page - 1)}
              >
                {t('previous', 'Previous')}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="btn-modern btn-secondary"
                disabled={!adminSubscriptionsPagination.has_next}
                onClick={() => setPage(page + 1)}
              >
                {t('next', 'Next')}
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Assign Subscription Modal */}
      <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)} centered className="modern-modal">
        <Modal.Header closeButton className="modern-modal-header">
          <Modal.Title>{t('assign_subscription', 'Assign Subscription')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.5rem' }}>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>
              {t('user', 'User')} *
            </Form.Label>
            {loadingUsers ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" style={{ color: 'var(--brand-primary)' }} />
              </div>
            ) : (
              <Form.Select
                value={inviteUserId}
                onChange={(e) => setInviteUserId(e.target.value ? Number(e.target.value) : '')}
                style={{
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-color)',
                  borderRadius: 10,
                }}
              >
                <option value="">{t('select_user', 'Select a user')}</option>
                {eligibleUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name} ({u.email})
                    {u.companies?.[0]?.company_name ? ` — ${u.companies[0].company_name}` : ''}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>

          {selectedUser && (
            <div
              className="mb-3 p-3"
              style={{
                background: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.05)',
                borderRadius: 12,
                border: '1px solid var(--border-color)',
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-2">
                <strong style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>{selectedUser.full_name}</strong>
                <Badge bg="info" style={{ fontSize: '0.7rem' }}>
                  {selectedUser.roles.find((r) => r === 'company_owner' || r === 'agency_owner') || selectedUser.roles[0]}
                </Badge>
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                <span className="me-3">
                  <strong>{t('email', 'Email')}:</strong> {selectedUser.email}
                </span>
              </div>
              {selectedUser.companies?.[0]?.company_name && (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
                  <strong>{t('company', 'Company')}:</strong> {selectedUser.companies[0].company_name}
                </div>
              )}
              <Form.Text style={{ color: 'var(--text-muted)', display: 'block', marginTop: 8 }}>
                {t('invite_email_help', 'The user will receive an email with a link to complete the payment.')}
              </Form.Text>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>
              {t('plan', 'Plan')} *
            </Form.Label>
            <Form.Select
              value={invitePlanId}
              onChange={(e) => setInvitePlanId(e.target.value ? Number(e.target.value) : '')}
              style={{
                background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)',
                borderRadius: 10,
              }}
            >
              <option value="">{t('select_plan', 'Select a plan')}</option>
              {adminPlans.filter((p) => p.is_active).map((p) => (
                <option key={p.id} value={p.id}>{p.name} — {p.price} {p.currency.toUpperCase()}/{p.interval}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="modern-modal-footer border-0 pt-0 pb-4 justify-content-end px-4">
          <Button variant="outline-secondary" className="btn-minimal" onClick={() => setShowInviteModal(false)} disabled={inviting}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            variant="info"
            className="btn-minimal"
            onClick={handleInvite}
            disabled={inviting || !inviteUserId || !invitePlanId}
          >
            {inviting ? <Spinner animation="border" size="sm" className="me-1" /> : <FaPaperPlane className="me-1" />}
            {t('send_invitation', 'Send Invitation')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SubscriptionsDashboardPage;
